import React__default, { useMemo } from 'react';
import cloneDeep from '../../../../../../../lodash.clonedeep/index.mjs';
import { ProductOptionContext } from './product-option-context.mjs';
import { imageValidator } from './validators/image-validator.mjs';
import { styleValidator } from './validators/style-validator.mjs';
import { priceDisplayModeValidator } from './validators/price-display-mode-validator.mjs';
import { styleDecorator } from './decorators/style-decorator.mjs';
import { descriptionValidator } from './validators/description-validator.mjs';
import { savingsValidator } from './validators/savings-validator.mjs';
import { isProductOptionModelList, containsListAndRange } from '../../../types/helpers/isProductOptionModelList.mjs';
import { useProductOptionsTranslations } from '../../product-options-translations/product-option-translations.mjs';
import { optionNameDecorator } from './decorators/option-name-decorator.mjs';
import { calloutDecorator } from './decorators/callout-decorator.mjs';
import { rangeDecorator } from './decorators/range-decorator.mjs';

const ProductOptionProvider = ({ productOption, skin, priceDisplayMode = "differential-unit", hideImages, hideDescriptions, showFormErrors = false, uiDensityQuantityUpdate = false, savingsFormat = "percent", children, }) => {
    const { i18n } = useProductOptionsTranslations();
    const listValidationsResult = useListValidations(productOption, savingsFormat, hideImages, priceDisplayMode, hideDescriptions, skin);
    const rangedValidationResult = useRangeValidations(productOption);
    const validationsResult = Object.assign({}, listValidationsResult, rangedValidationResult);
    const isValid = !productOption.isRequired || (productOption.isRequired && !!productOption.selectedKey);
    // run some logic here to decorate the product option
    const context = useMemo(() => {
        // run all the decorators here
        const cloned = cloneDeep(productOption);
        const decoratorMutations = [
            ...styleDecorator(cloned, validationsResult.style, validationsResult.skin).mutations,
            ...optionNameDecorator(cloned, i18n).mutations,
            ...calloutDecorator(cloned, i18n).mutations,
            ...rangeDecorator(cloned).mutations,
        ];
        return {
            validationMessages: validationsResult.messages,
            decoratorMutations,
            productOption: cloned,
            skin: validationsResult.skin,
            priceDisplayMode: validationsResult.priceDisplayMode,
            hideImages: validationsResult.hideImages,
            hideDescriptions: validationsResult.hideDescriptions,
            savingsFormat: validationsResult.savingsFormat,
            showFormErrors: showFormErrors && !isValid,
            uiDensityQuantityUpdate,
            selectedOptionText: getSelectedOptionText(productOption),
        };
    }, [productOption, validationsResult]);
    return React__default.createElement(ProductOptionContext.Provider, { value: context }, children);
};
function useRangeValidations(productOption) {
    return useMemo(() => {
        if (!isProductOptionModelList(productOption)) {
            return {
                style: "textbox",
                skin: "wide",
                messages: ["Ranged attributes only have one possible UI at this time."],
            };
        }
        if (containsListAndRange(productOption)) {
            return {
                messages: ["Ranged attributes can only have range options; list options filtered out"],
            };
        }
    }, [productOption]);
}
function useListValidations(productOption, savingsFormat, hideImages, priceDisplayMode, hideDescriptions, skin = "standard") {
    return useMemo(() => {
        if (isProductOptionModelList(productOption)) {
            // run all the validators here
            const { messages: savingsMessages, ...savingsRest } = savingsValidator(productOption, savingsFormat);
            const { messages: imageMessages, ...imageRest } = imageValidator(productOption, hideImages);
            const { messages: priceDisplayModeMessages, ...priceDisplayModeRest } = priceDisplayModeValidator(productOption, priceDisplayMode);
            const { messages: descriptionMessages, ...descriptionRest } = descriptionValidator(productOption, hideDescriptions);
            const { messages: styleMessages, ...styleRest } = styleValidator(productOption, skin, imageRest.hideImages, descriptionRest.hideDescriptions);
            return {
                hideImages: imageRest.hideImages,
                style: styleRest.style,
                skin: styleRest.skin,
                priceDisplayMode: priceDisplayModeRest.priceDisplayMode,
                hideDescriptions: descriptionRest.hideDescriptions,
                savingsFormat: savingsRest.savingsFormat,
                messages: [
                    ...imageMessages,
                    ...styleMessages,
                    ...priceDisplayModeMessages,
                    ...descriptionMessages,
                    ...savingsMessages,
                ],
            };
        }
    }, [skin, priceDisplayMode, hideImages, hideDescriptions, productOption, savingsFormat]);
}
function getSelectedOptionText(productOption) {
    if (isProductOptionModelList(productOption)) {
        return productOption.choices.find((c) => c.key === productOption.selectedKey)?.name;
    }
    return productOption.selectedKey;
}

export { ProductOptionProvider };
//# sourceMappingURL=product-context-provider.mjs.map
